import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, adminApi, formatIstDate, formatMoney } from '@wa/shared';
import { Cell, DataTable, Page } from '@/components/Primitives';

/**
 * What the platform charges, and corrections to a client's wallet.
 *
 * The defaults are the starting point for the next client, not a lever over the
 * existing ones: every tenant carries its own copy of the price list from the moment
 * it is created, so changing a default here does not silently re-price work already
 * quoted. That is stated on the page because the opposite is what people assume.
 */
export function PricingPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(null);

  const defaults = useQuery({ queryKey: ['admin', 'pricing'], queryFn: adminApi.defaultPricing });
  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: adminApi.tenants });
  const tenantPrices = useQuery({
    queryKey: ['admin', 'pricing', tenantId],
    queryFn: () => adminApi.tenantPricing(tenantId!),
    enabled: tenantId !== null,
  });

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const setDefault = useMutation({
    mutationFn: ({ category, price }: { category: string; price: number }) =>
      adminApi.setDefaultPrice(category, price),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['admin', 'pricing'] }); },
    onError: (e) => fail(e, 'Could not change that price.'),
  });

  const setTenant = useMutation({
    mutationFn: ({ category, price }: { category: string; price: number }) =>
      adminApi.setTenantPrice(tenantId!, category, price),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['admin', 'pricing', tenantId] }); },
    onError: (e) => fail(e, 'Could not change that price.'),
  });

  const adjust = useMutation({
    mutationFn: ({ amount, reason }: { amount: number; reason: string }) =>
      adminApi.adjustWallet(tenantId!, amount, reason),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] }); },
    onError: (e) => fail(e, 'Could not adjust that wallet.'),
  });

  const suspend = useMutation({
    mutationFn: ({ active, reason }: { active: boolean; reason?: string }) =>
      adminApi.suspendWallet(tenantId!, active, reason),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] }); },
    onError: (e) => fail(e, 'Could not change that account.'),
  });

  return (
    <Page title="Pricing" subtitle="What the platform charges, per message category.">
      {error && (
        <p role="alert" className="mb-3 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      <p className="mb-3 max-w-prose text-small text-(--color-muted)">
        These are the defaults a <b>new</b> tenant is created with. Every tenant keeps its own copy
        from that moment, so changing a default here does not re-price anybody already on the
        platform — which is deliberate: a client&rsquo;s bill should not move because we changed a
        list price after they were quoted.
      </p>

      <DataTable head={['Category', 'Default price', 'Updated', '']}>
        {defaults.data?.map((row) => (
          <tr key={row.category}>
            <Cell>{row.category}</Cell>
            <Cell nowrap>₹{formatMoney(row.unitPrice)}</Cell>
            <Cell nowrap>{formatIstDate(row.updatedAt)}</Cell>
            <Cell>
              <PriceInput
                current={row.unitPrice}
                disabled={setDefault.isPending}
                onSave={(price) => setDefault.mutate({ category: row.category, price })}
              />
            </Cell>
          </tr>
        ))}
      </DataTable>

      <h2 className="mt-6 mb-2 text-body font-semibold">One client</h2>

      <label className="mb-3 flex max-w-sm flex-col gap-1">
        <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Tenant</span>
        <select
          value={tenantId ?? ''}
          onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : null)}
          className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
        >
          <option value="">Choose a client…</option>
          {tenants.data?.map((t) => <option key={t.id} value={t.id}>{t.companyName}</option>)}
        </select>
      </label>

      {tenantId !== null && (
        <>
          <DataTable head={['Category', 'Their price', 'Platform default', '']}>
            {tenantPrices.data?.map((row) => (
              <tr key={row.category}>
                <Cell>{row.category}</Cell>
                <Cell nowrap>
                  ₹{formatMoney(row.unitPrice)}
                  {row.isOverride && (
                    <span className="ml-1.5 text-micro text-(--color-muted)">set for them</span>
                  )}
                </Cell>
                <Cell nowrap>₹{formatMoney(row.defaultPrice ?? 0)}</Cell>
                <Cell>
                  <PriceInput
                    current={row.unitPrice}
                    disabled={setTenant.isPending}
                    onSave={(price) => setTenant.mutate({ category: row.category, price })}
                  />
                </Cell>
              </tr>
            ))}
          </DataTable>

          <WalletControls
            isBusy={adjust.isPending || suspend.isPending}
            onAdjust={(amount, reason) => adjust.mutate({ amount, reason })}
            onSuspend={(active, reason) => suspend.mutate({ active, reason })}
          />
        </>
      )}
    </Page>
  );
}

function PriceInput({ current, disabled, onSave }: {
  current: number; disabled: boolean; onSave: (price: number) => void;
}) {
  const [value, setValue] = useState('');

  return (
    <span className="flex items-center gap-1.5">
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        placeholder={current.toFixed(2)}
        onChange={(e) => setValue(e.target.value)}
        className="w-24 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1 text-small"
      />
      <button
        type="button"
        disabled={disabled || value === '' || Number.isNaN(Number(value))}
        onClick={() => { onSave(Number(value)); setValue(''); }}
        className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken) disabled:opacity-40"
      >
        Set
      </button>
    </span>
  );
}

/**
 * A correction and a suspension, side by side, because they are the two things a
 * person reaches for when a client rings up about money.
 */
function WalletControls({ isBusy, onAdjust, onSuspend }: {
  isBusy: boolean;
  onAdjust: (amount: number, reason: string) => void;
  onSuspend: (active: boolean, reason?: string) => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  return (
    <section className="mt-6 rounded-lg border border-(--color-line) bg-(--color-surface) p-3 sm:p-4">
      <h2 className="text-body font-semibold">Correct or suspend</h2>
      <p className="mt-1 max-w-prose text-small text-(--color-muted)">
        A correction is a ledger entry beside the ones it corrects, never an edit to them &mdash;
        nothing in this product changes a wallet line after it is written. Suspending stops priced
        sends only; replies inside the 24-hour window still go out, because they cost nothing and
        stopping them just leaves a customer waiting.
      </p>

      <div className="mt-2.5 flex flex-wrap items-end gap-2">
        <label className="flex w-32 flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Amount</span>
          <input
            type="number"
            step="0.01"
            value={amount}
            placeholder="500 or -500"
            onChange={(e) => setAmount(e.target.value)}
            className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
        </label>

        <label className="flex min-w-48 flex-1 flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Reason</span>
          <input
            value={reason}
            placeholder="Goodwill credit — failed campaign on 12 Aug"
            onChange={(e) => setReason(e.target.value)}
            className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
        </label>

        <button
          type="button"
          disabled={isBusy || amount === '' || Number(amount) === 0 || reason.trim().length === 0}
          onClick={() => { onAdjust(Number(amount), reason.trim()); setAmount(''); setReason(''); }}
          className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-white disabled:opacity-40"
        >
          Apply
        </button>

        <span className="mx-1 h-6 w-px bg-(--color-line)" aria-hidden="true" />

        <button
          type="button"
          disabled={isBusy}
          onClick={() => onSuspend(false, reason.trim() || undefined)}
          className="tap rounded border border-(--color-line) px-2.5 py-1.5 text-small text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad)"
        >
          Suspend sending
        </button>

        <button
          type="button"
          disabled={isBusy}
          onClick={() => onSuspend(true)}
          className="tap rounded border border-(--color-brand) px-2.5 py-1.5 text-small text-(--color-brand)"
        >
          Resume
        </button>
      </div>
    </section>
  );
}
